"use client";

import { QueryCache, MutationCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "next-themes";
import { Suspense, useState } from "react";
import { ApiError } from "@/lib/api/error";
import { AttributionCapture } from "@/features/auth/components/attribution-capture";
import { isArbitratorSessionStaleQueryKey } from "@/lib/api/arbitrator-cache";
import { hashQueryKey } from "@/lib/json/safe-stringify";
import { handleQuotaError } from "@/lib/api/quota";
import { showToast } from "@/components/ui/sonner";
import { isOrgMissing } from "@/lib/permissions";
import { VoiceGlowOverlay } from "@/components/ui/voice-glow-overlay";

function shouldRetry(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiError) return false;
  return failureCount < 1;
}

let lastServerErrorToast = 0;

function handleServerError(error: unknown) {
  if (!(error instanceof ApiError) || error.status < 500) return;

  const now = Date.now();
  if (now - lastServerErrorToast < 3000) return;
  lastServerErrorToast = now;

  showToast.error("Something went wrong", {
    description: "An unexpected error occurred and our team has been notified.",
  });
}

function handleForbidden(client: QueryClient, error: unknown) {
  if (!(error instanceof ApiError) || error.status !== 403) return;

  client.invalidateQueries({ queryKey: ["auth", "me"] });
  client.invalidateQueries({ queryKey: ["projects"] });
  client.invalidateQueries({ queryKey: ["jobs"] });
}

function handleNoActiveOrg(client: QueryClient, error: unknown) {
  if (!(error instanceof ApiError)) return;
  if (!isOrgMissing(error)) return;

  client.invalidateQueries({ queryKey: ["auth", "me"] });
  client.invalidateQueries({ queryKey: ["projects"] });
  client.invalidateQueries({ queryKey: ["jobs"] });
}

export function createAppQueryClient(): QueryClient {
  // Declared first so the cache `onError` closures below can reference it;
  // assigned exactly once but cannot be `const` due to that forward use.
  // eslint-disable-next-line prefer-const
  let client: QueryClient;

  const queryCache = new QueryCache({
    onError: (error, query) => {
      if (handleQuotaError(error)) return;
      // Same opt-out as mutations: a poll that reports its own failures
      // (the export tracker) sets `meta: { suppressGlobalErrorToast: true }`.
      if (!query.meta?.suppressGlobalErrorToast) {
        handleServerError(error);
      }
      handleForbidden(client, error);
      handleNoActiveOrg(client, error);
    },
  });

  const mutationCache = new MutationCache({
    onSuccess: () => {
      // Credit balance updates via notification `credit_changed` — do not
      // refetch /credits on every mutation.
      // A stage change (pipeline advance/reject/move, etc.) alters arbitrator
      // stage membership, which drives `is_stale`. The originating code never
      // knows the arbitrator session id, so refresh the session/by-job-stage
      // queries here: active ones (on the arbitrator) refetch immediately and
      // update the "profiles may have changed" banner; inactive ones go stale
      // and refetch on next mount.
      client.invalidateQueries({
        predicate: (q) => isArbitratorSessionStaleQueryKey(q.queryKey),
      });
    },
    onError: (error, _variables, _context, mutation) => {
      if (handleQuotaError(error)) return;
      // A mutation can opt out of the generic "Something went wrong" toast
      // (e.g. when the calling component shows its own specific message) by
      // setting `meta: { suppressGlobalErrorToast: true }`.
      if (!mutation?.meta?.suppressGlobalErrorToast) {
        handleServerError(error);
      }
      handleForbidden(client, error);
      handleNoActiveOrg(client, error);
    },
  });

  client = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        refetchOnMount: true,
        refetchOnWindowFocus: true,
        retry: shouldRetry,
        // Default `hashKey` JSON.stringifies the query key and throws
        // "Converting circular structure to JSON" if a key ever references a
        // DOM node (e.g. a React-managed input leaking into filters). Use a
        // circular-/DOM-safe hasher so a stray reference can't crash render.
        queryKeyHashFn: hashQueryKey,
      },
    },
    queryCache,
    mutationCache,
  });

  return client;
}

export function Providers({ children, nonce }: { children: React.ReactNode; nonce?: string }) {
  const [queryClient] = useState(createAppQueryClient);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider
        attribute="class"
        defaultTheme="dark"
        enableSystem
        disableTransitionOnChange
        nonce={nonce}
        // React 19 does not run <script> inserted by a client component and logs
        // an error. The blocking script lives in the root layout instead.
        // A non-JS type marks this copy as a data block so that error stays quiet.
        scriptProps={{ type: "application/json" }}
      >
        {/* Suspense: useSearchParams in AttributionCapture requires a boundary. */}
        <Suspense fallback={null}>
          <AttributionCapture />
        </Suspense>
        {children}
        <VoiceGlowOverlay />
      </ThemeProvider>
    </QueryClientProvider>
  );
}
